import AddIcon from '@mui/icons-material/Add'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import { Alert, Avatar, Button, CircularProgress, Typography } from '@mui/material'
import { useState } from 'react'

import { Box } from '@/components/ui'
import type { IntakeFormListItem } from '@/definitions/intakeForm'
import { useConfirm } from '@/hooks/useConfirm'
import { useIntakeForms } from '@/hooks/useIntakeForms'
import { useSetIntakeFormEnabled } from '@/hooks/useSetIntakeFormEnabled'
import { PageCondensed } from '@/layouts'

import { useRouteOrg } from '../../orgRoute'
import { EditGenericFormModal } from './components/EditGenericFormModal'
import { IntakeFormRow } from './components/IntakeFormRow'
import { ReplaceIntakeFormModal } from './components/ReplaceIntakeFormModal'
import { UploadIntakeFormModal } from './components/UploadIntakeFormModal'
import {
  intakeFormDeactivationCopy,
  intakeFormEnabledActionLabel,
  intakeFormEnabledSuccessMessage,
} from './utils/intakeFormEnabledCopy'

const FORMS_UNREADABLE_MESSAGE =
  'This organization’s intake forms could not be loaded. Reload to try again.'

const NO_FORMS_MESSAGE =
  'This organization has no intake form on record, so it has no form to hand a ' +
  'patient. A generic form is created as part of setting an organization up, so an ' +
  'organization without one predates that or did not finish setup.'

/**
 * An organization's intake forms, and the box for adding a custom one.
 *
 * One request carries every form, so the tab either has all of them or reports the
 * one failure. Each entry is a form's current state rather than one of its
 * revisions — editing a form adds a revision under the same identity, and only the
 * newest is ever handed to a patient. Rows are in the order the API returns them,
 * which is by name.
 *
 * Four changes are available: adding a custom form, replacing a custom form's
 * questions under the link already handed out, editing the generic form's title
 * and text, and switching any form off or back on. Replacing a switched-off form
 * leaves it off, and which form is primary is not chosen here.
 */
export const IntakeFormsTab = () => {
  const org = useRouteOrg()

  const confirm = useConfirm()
  const { data: forms, isLoading, isError, isFetching, refetch } = useIntakeForms(org.id)
  const { mutate: setFormEnabled, isPending, variables } = useSetIntakeFormEnabled()
  const pendingFormId = isPending ? variables.baseFormId : null
  // Held until the refreshed list arrives: the status chip and the action's label
  // are what say the change landed, and both wait on the refetch.
  const isActionDisabled = isPending || isFetching

  const handleSetEnabled = async (form: IntakeFormListItem, enabled: boolean) => {
    if (!enabled) {
      const copy = intakeFormDeactivationCopy(form, org.intake_automation_enabled)
      const confirmed = await confirm({
        title: copy.title,
        body: (
          <Box variant='flexColumn' gap='md'>
            <Alert severity='warning'>
              {copy.effect}
              {copy.inUseNote !== undefined && ` ${copy.inUseNote}`}
              {` ${copy.remindersNote}`}
            </Alert>
            <Typography variant='body2' color='text.secondary'>
              {copy.reversal}
            </Typography>
          </Box>
        ),
        confirmLabel: intakeFormEnabledActionLabel(true),
        destructive: true,
      })
      if (!confirmed) {
        return
      }
    }

    setFormEnabled({
      orgId: org.id,
      baseFormId: form.base_form_id,
      enabled,
      successMessage: intakeFormEnabledSuccessMessage(form, enabled),
    })
  }
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  // Held rather than a boolean so the box knows which form it is editing, and so
  // it unmounts on close and cannot reopen holding the previous form's text.
  const [editingForm, setEditingForm] = useState<IntakeFormListItem | null>(null)
  const [replacingForm, setReplacingForm] = useState<IntakeFormListItem | null>(null)

  // A write that timed out may commit after its own refresh ran, so the list is
  // re-read whenever a box closes.
  const closeUpload = () => {
    setIsUploadOpen(false)
    void refetch()
  }
  const closeEdit = () => {
    setEditingForm(null)
    void refetch()
  }
  const closeReplace = () => {
    setReplacingForm(null)
    void refetch()
  }

  const rows = forms ?? []
  const hasNoForms = !isLoading && !isError && rows.length === 0

  return (
    <PageCondensed>
      <Box variant='flexColumn' gap='lg'>
        <Box variant='flexBetween'>
          <Box variant='flex' gap='md'>
            <Avatar variant='titleIcon'>
              <AssignmentOutlinedIcon />
            </Avatar>
            <Typography variant='h4' fontWeight={600}>
              Intake Forms
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setIsUploadOpen(true)}
          >
            Add Custom Form
          </Button>
        </Box>

        {isLoading && (
          <Box variant='flexCenter'>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity='error'>{FORMS_UNREADABLE_MESSAGE}</Alert>}

        {hasNoForms && (
          <Typography variant='body2' color='text.secondary'>
            {NO_FORMS_MESSAGE}
          </Typography>
        )}

        {rows.length > 0 && (
          <Box variant='flexColumn' gap='sm'>
            {rows.map((form) => (
              <IntakeFormRow
                key={form.base_form_id}
                form={form}
                onEdit={setEditingForm}
                onReplace={setReplacingForm}
                isActionPending={pendingFormId === form.base_form_id}
                isActionDisabled={isActionDisabled}
                onSetEnabled={(enabled) => handleSetEnabled(form, enabled)}
              />
            ))}
          </Box>
        )}
      </Box>

      {isUploadOpen && (
        <UploadIntakeFormModal orgId={org.id} onClose={closeUpload} />
      )}

      {replacingForm !== null && (
        <ReplaceIntakeFormModal
          orgId={org.id}
          form={replacingForm}
          onClose={closeReplace}
        />
      )}

      {editingForm !== null && (
        <EditGenericFormModal
          orgId={org.id}
          baseFormId={editingForm.base_form_id}
          enabled={editingForm.enabled}
          onClose={closeEdit}
        />
      )}
    </PageCondensed>
  )
}
